import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { RequireAuth, useDistributionPrincipal } from '@dss/auth'
import { Card, Button, Banner, Icon } from '@dss/design-system'
import { AdminShell } from '../components/AdminShell'
import { Integration1cHealthCard } from '../features/exchange-1c/Integration1cHealthCard'
import { StuckDistributorsWidget } from '../features/distributors/StuckDistributorsWidget'
import { useDashboardSummary } from '../features/dashboard/useDashboardSummary'
import { useDashboardActivity } from '../features/dashboard/useDashboardActivity'
import { NewOrdersWidget } from '../features/dashboard/NewOrdersWidget'
import { PendingOrdersWidget } from '../features/dashboard/PendingOrdersWidget'
import { HierarchyWarningsWidget } from '../features/dashboard/HierarchyWarningsWidget'
import { RecentActivityWidget } from '../features/dashboard/RecentActivityWidget'
import { useOnboardingState } from '../features/onboarding/useOnboardingState'

/** Permission, гейтирующий Recent activity feed (follow-up DO-5).
 *  Backend требует `distributor.audit.view` — есть у OWNER, нет у MANAGER.
 *  Скрываем widget на фронте, чтобы MANAGER не видел пустой блок с 403. */
const ACTIVITY_PERMISSION = 'distributor.audit.view'

/** Dashboard — DO-5 / D18 §D18-5 + E3.5 / D19 §D19-3 (auto-redirect onboarding).
 *
 *  Три ветки:
 *  - PA: StuckDistributorsWidget + CTA-карточка на /distributors. Не вызывает
 *    /admin/dashboard/summary (нет permission distributor.orders.view → 403).
 *  - OWNER/MANAGER: 3 counter-виджета (новые заказы, pending-orders,
 *    hierarchy warnings). Под виджетами — compact Integration1cHealthCard.
 *
 *  E3.5: для DISTRIBUTOR_OWNER с незавершённым онбордингом — авто-редирект
 *  на `/onboarding`. Старый DO-5 onboarding-checklist widget удалён (replaced
 *  full-page wizard'ом). */
export const Route = createFileRoute('/dashboard')({
  component: DashboardScreen,
})

function DashboardScreen() {
  return (
    <RequireAuth>
      <DashboardContent />
    </RequireAuth>
  )
}

function DashboardContent() {
  const principal = useDistributionPrincipal()
  const isPlatformAdmin = Boolean(principal?.isPlatformAdmin)
  const isOwner = principal?.roles?.includes('DISTRIBUTOR_OWNER') ?? false

  // E3.5: автопереход на /onboarding для OWNER'а с незавершённым wizard'ом.
  // Запрос /admin/onboarding/state включаем только для OWNER (у MANAGER/FIELD/PA
  // permission `distributor.settings.manage` нет → бекенд вернёт 403).
  const onboarding = useOnboardingState(isOwner && !isPlatformAdmin)
  const navigate = useNavigate()
  useEffect(() => {
    if (isOwner && !isPlatformAdmin && onboarding.data && !onboarding.data.completed) {
      navigate({ to: '/onboarding' })
    }
  }, [isOwner, isPlatformAdmin, onboarding.data, navigate])

  return (
    <AdminShell active="dashboard" crumbs={<strong>Дашборд</strong>}>
      <div className="page-head">
        <div>
          <h1>Дашборд</h1>
          <p className="sub">
            {isPlatformAdmin
              ? 'Платформенные виджеты. Основные действия — в разделе «Дистрибьюторы».'
              : 'Новые заказы, pending-orders, изменения иерархии 1С.'}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* PA-5: condition-only widget, прячется на не-PA пользователях. */}
        <StuckDistributorsWidget />

        {isPlatformAdmin ? <PaSection /> : <DistributorSection />}
      </div>
    </AdminShell>
  )
}

/** PA-секция: CTA на /distributors. /admin/dashboard/summary не вызываем
 *  (нет permission → 403). */
function PaSection() {
  return (
    <Card padding="lg" radius="md">
      <p style={{ margin: '0 0 16px', color: 'var(--color-text-secondary)' }}>
        Список тенантов, создание новых, recovery OWNER&apos;а и ручные
        переходы статусов — на странице «Дистрибьюторы».
      </p>
      <Link
        to="/distributors"
        className="btn btn--primary"
        style={{ textDecoration: 'none' }}
        data-testid="dashboard-pa-distributors-cta"
      >
        <Icon name="Box" size="sm" />
        Открыть «Дистрибьюторы»
      </Link>
      {/* Button здесь — для tree-shake guard'а. */}
      <Button variant="ghost" style={{ display: 'none' }}>noop</Button>
    </Card>
  )
}

/** OWNER/MANAGER-секция: 3 counter'а + Recent activity feed (OWNER-only) +
 *  compact health-card. Onboarding-checklist widget удалён в E3.5
 *  (replaced full-page wizard'ом на /onboarding). */
function DistributorSection() {
  const principal = useDistributionPrincipal()
  const canViewActivity = Boolean(principal?.permissions?.includes(ACTIVITY_PERMISSION))
  const summary = useDashboardSummary()
  const activity = useDashboardActivity(canViewActivity)
  const data = summary.data

  return (
    <>
      {summary.isError ? (
        <Banner variant="danger" title="Не удалось загрузить данные дашборда">
          Виджеты показываются пустыми. Повторите попытку через минуту —
          поллинг продолжит работу автоматически.
        </Banner>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}
        data-testid="dashboard-counter-grid"
      >
        <NewOrdersWidget count={data?.newOrders} isLoading={summary.isLoading} />
        <PendingOrdersWidget count={data?.pendingOrders} isLoading={summary.isLoading} />
        <HierarchyWarningsWidget
          count={data?.hierarchyWarnings}
          isLoading={summary.isLoading}
        />
      </div>

      {canViewActivity ? (
        <RecentActivityWidget
          items={activity.data}
          isLoading={activity.isLoading}
          isError={activity.isError}
        />
      ) : null}

      <Integration1cHealthCard variant="compact" />
    </>
  )
}
